import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

import {
  AddSignatureMatrixTemplate,
  ClearError,
  DisciplineLoad,
  ProjectUsers,
  SignatureRoleLoad,
  UpdateSignatureMatrixTemplate,
} from '@completion/actions';
import { ButtonStatus, ResourceStatus, ResourceType } from '@completion/enums';
import { SignatureMatrixTemplate, SignatureRole, User, Discipline } from '@completion/models';
import { getResourceState, getSignatureRoles, projectUsers, getDisciplines } from '@completion/selectors';
import { hasSameId } from '@completion/utils';

@Component({
  selector: 'app-matrix-template-edit-dialog',
  templateUrl: './matrix-template-edit-dialog.component.html',
  styleUrls: ['./matrix-template-edit-dialog.component.scss'],
  standalone: false,
})
export class MatrixEditDialogComponent implements OnInit {
  hasSameIds = hasSameId;

  buttonStatus = ButtonStatus.IDLE;
  errorMsg: string | null;
  form: UntypedFormGroup;

  signatureMatrixTemplate: SignatureMatrixTemplate | undefined;
  private readonly destroy$: Subject<void> = new Subject();

  signatureRoles$: Observable<SignatureRole[]> = this.store.pipe(select(getSignatureRoles), takeUntil(this.destroy$));
  signees$: Observable<User[]> = this.store.pipe(select(projectUsers), takeUntil(this.destroy$));
  disciplines$: Observable<Discipline[]> = this.store.pipe(select(getDisciplines), takeUntil(this.destroy$));

  constructor(
    private readonly store: Store,
    private readonly formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<MatrixEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data = null,
  ) {
    this.store.dispatch(new SignatureRoleLoad());
    this.store.dispatch(new ProjectUsers());
    this.store.dispatch(new DisciplineLoad());
    this.signatureMatrixTemplate = { ...this.data.signatureMatrixTemplate };

    this.store
      .pipe(select(getResourceState, ResourceType.UpdateSignatureMatrixTemplate), skip(1), takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state.status === ResourceStatus.InProgress) {
          this.buttonStatus = ButtonStatus.IN_PROGRESS;
        }

        if (state.status === ResourceStatus.Failure) {
          this.buttonStatus = ButtonStatus.IDLE;
        }

        this.errorMsg = state.lastError;

        if (state.status === ResourceStatus.Success) {
          this.buttonStatus = ButtonStatus.COMPLETED;
          setTimeout(() => {
            this.buttonStatus = ButtonStatus.IDLE;
            this.dialogRef.close();
          }, 1000);
        }
      });
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      ordinal: [this.signatureMatrixTemplate.ordinal],
      role: [this.signatureMatrixTemplate.role],
      verified: [this.signatureMatrixTemplate.verified],
      signee: [this.signatureMatrixTemplate.signee],
      signatureRole: [this.signatureMatrixTemplate.signatureRole],
      discipline: [this.signatureMatrixTemplate.discipline],
    });
  }

  onSave(): void {
    this.form.markAllAsTouched();
    this.errorMsg = null;
    this.store.dispatch(new ClearError());

    if (this.form.valid) {
      this.buttonStatus = ButtonStatus.IN_PROGRESS;
      if (this.signatureMatrixTemplate.id) {
        this.store.dispatch(new UpdateSignatureMatrixTemplate(this.signatureMatrixTemplate));
      } else {
        this.store.dispatch(new AddSignatureMatrixTemplate(this.signatureMatrixTemplate));
      }

      if (!this.errorMsg || this.errorMsg.length === 0) {
        this.buttonStatus = ButtonStatus.COMPLETED;
        setTimeout(() => (this.buttonStatus = ButtonStatus.IDLE), 2000);
        this.dialogRef.close();
      }
    }
  }
}
